import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import adminApi, { setAdminToken } from "@/lib/adminApi";
import BrandMark from "@/components/BrandMark";
import Footer from "@/components/Footer";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", stay_signed_in: false });
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [resetSent, setResetSent] = useState(null);

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.post("/login", form);
      setAdminToken(data.session_token);
      toast.success("Signed in");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.post("/reset-password/request", { email: form.email });
      setResetSent(data);
      toast.success("If that email exists, a reset link was sent.");
    } catch (err) {
      toast.error("Could not send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <BrandMark variant="center" className="mb-8" />
          <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-4 text-center">
            Aynora · Admin
          </div>
          <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight tracking-tight text-stone-900 mb-8 text-center">
            {forgot ? "Reset password" : "Sign in"}
          </h1>
          <div className="flex flex-col gap-4">
            <input
              data-testid="admin-email-input"
              className="input"
              type="email"
              placeholder="admin@aynora.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            {!forgot && (
              <input
                data-testid="admin-password-input"
                className="input"
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            )}
            {!forgot && (
              <label className="flex items-center gap-2 text-sm font-[Manrope] text-stone-600">
                <input
                  type="checkbox"
                  className="accent-[#6B7A54]"
                  checked={form.stay_signed_in}
                  onChange={(e) => setForm({ ...form, stay_signed_in: e.target.checked })}
                />
                Stay signed in (30 days)
              </label>
            )}
            <button
              data-testid={forgot ? "admin-send-reset-btn" : "admin-login-btn"}
              onClick={forgot ? sendReset : submit}
              disabled={loading}
              className="btn-primary"
            >
              {loading ? "Working…" : forgot ? "Send reset link" : "Sign in"}
            </button>
            {resetSent?.mock_link && (
              <div className="rounded-2xl border border-[#6B7A54]/30 bg-[#F2EFEA] p-3 text-xs font-[Manrope] text-stone-700">
                <div className="font-bold mb-1">Mock email link (email delivery not configured):</div>
                <Link to={resetSent.mock_link} className="text-[#6B7A54] underline break-all">
                  {resetSent.mock_link}
                </Link>
              </div>
            )}
            <button
              onClick={() => { setForgot(!forgot); setResetSent(null); }}
              className="text-xs font-[Manrope] text-stone-500 underline underline-offset-4 self-center"
            >
              {forgot ? "Back to sign in" : "Forgot password?"}
            </button>
          </div>
        </div>
      </div>
      <div className="pb-6"><Footer /></div>
    </div>
  );
}
